import { useAgentSelector, useAgentStepData } from "@/modules/agent/states";
import { AgentSessionStatus, AgentSessionStepType } from "@/modules/sessions/types";
import { useParams } from "react-router-dom";
/* eslint-disable prettier/prettier */
import { VendorRiskSectionEnum } from "@/modules/agent/types/vendorAssessment";
import { useVendorRiskAssessment } from "../useVendorRiskAssessment/useVendorRiskAssessment";
import { useVendorRiskAssessmentStore } from "../useVendorRiskAssessment/useVendorRiskAssessmentStore";

import {
  VENDOR_BUSINESS_CRITICALITY,
  VENDOR_DATA_TYPES_HANDLED
} from "@/modules/agent/constants/vendorAssessmentConstants";
import {
  VendorOverviewKey,
  VendorOverviewKeyToValueTypeMap
} from "@/modules/agent/types/vendorOverviewSection";
import {
  DropDownAndTextAreaCard,
  DropDownCard,
  RiskAndJustitificationCard,
  TextAreaCard
} from "./OverviewCards";

export interface IdParams {
  id: string;
}

const Overview = () => {
  const { vendorOverview } = useVendorRiskAssessment();
  const setVendorOverview = useVendorRiskAssessmentStore().setVendorOverview;
  const { setSelectedId } = useAgentSelector.useActions();

  const { id } = useParams<keyof IdParams>() as IdParams;

  const stepData = useAgentStepData(id);
  const isReportReady =
    stepData?.find((step) => step.type === AgentSessionStepType.PREPARE_REPORT)
      ?.status === AgentSessionStatus.COMPLETE;

  const getFieldValue = <
    K extends VendorOverviewKey,
    V extends keyof VendorOverviewKeyToValueTypeMap[K]
  >(
    fieldKey: K,
    valueKey: V
  ) => {
    const item = vendorOverview.find(
      (overviewItem) => overviewItem.key === fieldKey
    );
    return item?.value ? (item.value as VendorOverviewKeyToValueTypeMap[K])[valueKey] : [];
  };

  const handleFieldValueChange = <
    K extends VendorOverviewKey,
    V extends keyof VendorOverviewKeyToValueTypeMap[K]
  >(
    fieldKey: K,
    valueKey: V,
    newValue: any
  ) => {
    const indexOfOverviewItem = vendorOverview.findIndex(
      (overviewItem) => overviewItem.key === fieldKey
    );
    
    if (indexOfOverviewItem === -1) return;
    
    // Create a deep copy to avoid mutating the original store data
    // This ensures changesMade counter increments properly
    const newVendorOverview = vendorOverview.map((item, idx) => {
      if (idx === indexOfOverviewItem) {
        return {
          ...item,
          value: {
            ...item.value,
            [valueKey]: newValue
          }
        };
      }
      return item;
    });
    
    // Set selectedId so auto-save knows which control to emit socket update for
    setSelectedId(id, fieldKey);
    
    // Get store actions
    const store = useVendorRiskAssessmentStore.getState();
    
    // Set saving state and update overview data
    store.setIsSaving(true);
    setVendorOverview(newVendorOverview);

    // Update combinedVendorRiskData as well
    if (store.combinedVendorRiskData) {
      const updatedCombinedData = {
        ...store.combinedVendorRiskData,
        [VendorRiskSectionEnum.vendor_overview]: newVendorOverview
      };
      store.setCombinedVendorRiskData(updatedCombinedData);
    }

    // Note: setVendorOverview above already increments changesMade counter
    // which triggers the debounced auto-save in useVendorRiskAssessment hook
    // The auto-save will then emit the socket update (see vendor.use-case.ts)
  };

  return (
    <div className="p-6 grid gap-4 grid-cols-2 w-full bg-white">
      <div className="col-span-2">
        <RiskAndJustitificationCard
          title="Risk & Justification"
          riskLevel={getFieldValue("risk_rating", "value")}
          updateRiskLevel={(newValue: VendorOverviewKeyToValueTypeMap["risk_rating"]["value"]) =>
            handleFieldValueChange("risk_rating", "value", newValue)
          }
          handleTextAreaValueChange={(newValue: VendorOverviewKeyToValueTypeMap["risk_rating"]["justification"]) =>
            handleFieldValueChange("risk_rating", "justification", newValue)
          }
          textAreaValue={getFieldValue("risk_rating", "justification") ?? ""}
          isDisabled={isReportReady}
          sessionId={id}
          controlId="risk_rating"
        />
      </div>
      <div className="col-span-2">
        <DropDownCard
          isDisabled={isReportReady}
          title="Business Criticality"
          selectionMode="single"
          // Format dropdown options correctly with value and label properties

          dropDownOptions={(isReportReady && !getFieldValue("business_criticality", "business_criticality").length)
            ? [{ value: "-", label: "-" }] 
            : [...VENDOR_BUSINESS_CRITICALITY]
          }
          sessionId={id}
          handleDropDownChanges={(newValues: VendorOverviewKeyToValueTypeMap["business_criticality"]["value"]["business_criticality"]) => {
            handleFieldValueChange("business_criticality", "business_criticality", newValues[0])
          }}
          // Make sure the dropdown values match the expected format
          dropdownValues={(isReportReady && !getFieldValue("business_criticality", "business_criticality").length)
            ? ["-"] 
            : [getFieldValue("business_criticality", "business_criticality") || ""]}
          controlId="business_criticality"
        />
      </div>
     
      <TextAreaCard
        title="Integration Requirements"
        handleTextAreaValueChange={(newValue: VendorOverviewKeyToValueTypeMap["integration_overview"]["value"]) =>
          handleFieldValueChange("integration_overview", "value", newValue)
        }
        textAreaValue={getFieldValue("integration_overview", "value") ?? ""}
        isDisabled={isReportReady}
        sessionId={id}
        controlId="integration_overview"
      />
      {/* <ComplianceAdherenceCard
        title="Compliance Adherence"
        values={getFieldValue("compliance", "value") ?? []}
        listOfExtraOptions={
          (vendorOverview.find(
            (overviewItem) => overviewItem.key === "compliance"
          ) as VendorRiskResponseItemForVendorOverview<"compliance">)
            ?.customer_added_certifications ?? []
        }
        handleChange={(newValues: VendorOverviewKeyToValueTypeMap["compliance"]["value"]) =>
          handleFieldValueChange("compliance", "value", newValues)
        }
        isDisabled={isReportReady}
        sessionId={id}
        controlId="compliance"
      /> */}
      <DropDownAndTextAreaCard
        title="Data Types Handled"
        selectionMode="multiple"
        dropDownOptions={[...VENDOR_DATA_TYPES_HANDLED]}
        handleDropDownChanges={(newValues: VendorOverviewKeyToValueTypeMap["data_types_handled"]["data_types"]) =>
          handleFieldValueChange("data_types_handled", "data_types", newValues)
        }
        dropdownValues={getFieldValue("data_types_handled", "data_types") ?? []}
        textAreaValue={getFieldValue("data_types_handled", "purpose") ?? ""}
        handleTextAreaValueChange={(newValue: VendorOverviewKeyToValueTypeMap["data_types_handled"]["purpose"]) =>
          handleFieldValueChange("data_types_handled", "purpose", newValue)
        }
        otherValues={getFieldValue("data_types_handled", "other_data_types") ?? ""}
        handleOtherValueChange={(newValue: VendorOverviewKeyToValueTypeMap["data_types_handled"]["other_data_types"]) =>
          handleFieldValueChange("data_types_handled", "other_data_types", newValue)
        }
        isDisabled={isReportReady}
        sessionId={id}
        controlId="data_types_handled"
      />
      {/* <DropDownAndTextAreaCard
        title="Incidents in Last 36 Months"
        selectionMode="single"
        dropDownOptions={[...VENDOR_INCIDENTS_IN_LAST_36_MONTHS]}
        handleDropDownChanges={(newValues: VendorOverviewKeyToValueTypeMap["security_incidents"]["incident_history"]) =>
          handleFieldValueChange(
            "security_incidents",
            "incident_history",
            newValues[0]
          )
        }
        dropdownValues={[
          getFieldValue("security_incidents", "incident_history"),
        ]}
        textAreaValue={
          getFieldValue("security_incidents", "incident_details") ?? ""
        }
        handleTextAreaValueChange={(newValue: VendorOverviewKeyToValueTypeMap["security_incidents"]["incident_details"]) =>
          handleFieldValueChange(
            "security_incidents",
            "incident_details",
            
            newValue
          )
        }
        isDisabled={isReportReady}
        sessionId={id}
        controlId="security_incidents"
      /> */}
    </div>
  );
};

export default Overview;